import { eventBus, EVENTS } from '@bao/events'
import { sectionRenderer } from '@bao/section-rendering'
import { fetchJSON, getDefaultRequestConfig, getElement } from '@bao/utils'

const defaultSectionsUrl = () => {
  const appProxyUrls = [`/apps/`, `/a/`, `/tools/`, `/community/`]

  if (appProxyUrls.some((url) => window.location.pathname.includes(url))) {
    return `/`
  }

  return window.location.pathname
}

/** @const {string} The context in which we want the sections to be rendered in */
export const DEFAULT_SECTIONS_URL = defaultSectionsUrl()

/**
 * @typedef {Object} PotentialCartItem
 *
 * @property {number} id The variant's unique ID
 * @property {number} quantity The quantity of items to be added to the cart
 * @property {Object} [properties] Line item property key/values
 */

/**
 * @typedef {Object} BaoCartSectionOptions
 *
 * @property {string[]} [sections] - The sections we want rendered alongside any cart
 *   changes
 * @property {string} [sectionsUrl] - The context in which we want the sections to be
 *   rendered in in
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithAttributes
 *
 * @property {Object} [attributes] Key/values that we want to add to the cart attributes
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithForm
 *
 * @property {HTMLFormElement} [form] The form that has added the item
 */

/**
 * By Association Only's cart helper methods
 */
class BaoCart {
  /**
   * @param {HTMLFormElement} form
   * @param {BaoCartSectionOptionsWithAttributes} options Optional values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItemFromForm (
    form,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const formData = new FormData(form)
    formData.append('sections', sections)
    formData.append('sections_url', sectionsUrl)

    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM, {
      form,
      formData,
    })

    const config = getDefaultRequestConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    try {
      const state = await fetchJSON(`${window.routes.cartAddUrl}`, config)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM, {
        form,
        formData,
        state,
      })

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)

      const pushCartSections = sectionRenderer.pushCartSections

      if (!pushCartSections || !pushCartSections.length) return

      sectionRenderer
        .fetch(pushCartSections.map((s) => s.section))
        .then((htmlSections) => {
          const updatedSections = pushCartSections.map((section) => ({
            ...section,
            html: htmlSections[section.section],
          }))

          sectionRenderer.renderMultiple(updatedSections)

          const cartCountHTML = htmlSections['push-cart-count']

          if (cartCountHTML) {
            eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, {
              state: {
                sections: {
                  'push-cart-count': cartCountHTML,
                },
              },
            })
          }
        })
        .catch((err) => {
          console.error('Failed to fetch cart sections:', err)
        })
    }
  }

  /**
   * @param {PotentialCartItem[]|PotentialCartItem} items
   * @param {BaoCartSectionOptionsWithAttributes & BaoCartSectionOptionsWithForm} options Optional
   *   values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItem (
    items,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      attributes = {},
      form,
    } = {},
  ) {
    if (!Array.isArray(items)) {
      items = [items]
    }

    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_ADD, {
      items,
      attributes,
      form,
    })

    const body = {
      items,
      attributes,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartAdd(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_ADD, {
        items,
        state,
        form,
      })

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The amount of the item we want change the line item to. Can be 0.
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateQuantity (
    key,
    quantity,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY, {
      key,
      quantity,
    })
    const body = {
      id: key,
      quantity,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY, {
        state,
      })

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItem (
    key,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM, { key })
    const body = {
      id: key,
      quantity: 0,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM, {
        state,
      })

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string[]} keys The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItems (
    keys,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM, {
      keys,
    })

    const body = {
      updates: Object.fromEntries(keys.map((k) => [k, 0])),
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM, {
        state,
      })
      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The quantity of items to be added to the cart. If this isn't set then Shopify will reset it back to 1
   * @param {object} properties An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateItemProperties (
    key,
    quantity,
    properties,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const body = { id: key, properties, quantity }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      return this.cartChange(body)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {object} attributes An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartAttributes (
    attributes,
    {
      sections = sectionRenderer.pushCartSections.map((s) => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    eventBus.dispatchEvent(EVENTS.PUSH_CART.BEFORE_UPDATE_CART_ATTRIBUTES, {
      attributes,
    })

    const body = { attributes }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      eventBus.dispatchEvent(EVENTS.PUSH_CART.AFTER_UPDATE_CART_ATTRIBUTES, {
        attributes,
      })

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Refresh push cart sections from the server and notify listeners
   *
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object|null>}
   */
  async refreshPushCart ({
    sections = sectionRenderer.pushCartSections.map((s) => s.section),
    sectionsUrl = DEFAULT_SECTIONS_URL,
  } = {}) {
    try {
      if (!sections.length) return null

      const htmlSections = await sectionRenderer.fetch(sections, sectionsUrl)

      const updatedSections = sectionRenderer.pushCartSections
        .map((section) => ({
          ...section,
          html: htmlSections[section.section],
        }))
        .filter((section) => section.html)

      if (updatedSections.length) {
        sectionRenderer.renderMultiple(updatedSections)
      }

      const state = { sections: htmlSections }

      eventBus.dispatchEvent(EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error('Failed to refresh push cart:', err)
      return null
    }
  }

  /**
   * @param {string} note The note we want to set
   *
   * @returns {Promise<Object>}
   */
  async updateNote (note) {
    eventBus.dispatchEvent(EVENTS.CART_NOTE.BEFORE_CHANGE, {
      note,
    })

    try {
      const state = await this.cartUpdate({ note })

      eventBus.dispatchEvent(EVENTS.CART_NOTE.AFTER_CHANGE, {
        state,
        note,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  handleAddToCartError (res) {
    /**
     * @type {PushCart}
     */
    const pushCart = getElement('push-cart')
    if (!pushCart) return

    pushCart.handleError(res)
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartAdd (body) {
    return fetchJSON(`${window.routes.cartAddUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartChange (body) {
    return fetchJSON(`${window.routes.cartChangeUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartUpdate (body) {
    return fetchJSON(`${window.routes.cartUpdateUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }
}

export const Cart = new BaoCart()

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return

  Cart.refreshPushCart()
})
